import { PermissionTypes, RouteWisePermission } from '@app/constants/enums/permission';
import { ADMIN_BASE_BATH, SOCKET_DEAL_ROOM_MESSAGE_FOR_ADMIN, SOCKET_DEAL_ROOM_UNREAD } from '@app/constants/variables';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { useResponsive } from '@app/hooks/useResponsive';
import {
  contentDirection,
  getAdminNotificationMessageKeyOnChat,
  getPermissionType,
  getRoutePath,
} from '@app/utils/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Header } from '../../../header/Header';
import MainContent from '../MainContent/MainContent';
import { MainHeader } from '../MainHeader/MainHeader';
import MainSider from '../sider/MainSider/MainSider';
import * as S from './MainLayout.styles';
import { IDealRoomMessage } from '@app/interfaces/deal-room';
import { notificationController } from '@app/controllers/notificationController';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import socket from '@app/utils/socket';
import {
  setDealRoomChat,
  setDealRoomClickMessage,
  setDealRoomMessage,
  setUnreadMessages,
} from '@app/store/slices/dealRoomSlice';
import { getUnreadMessage } from '@app/api/channel.api';
import { getUnreadDealRoom } from '@app/api/deal-room.api';

const MainLayout: React.FC = () => {
  const { t } = useTranslation();
  const [isTwoColumnsLayout, setIsTwoColumnsLayout] = useState(true);
  const [siderCollapsed, setSiderCollapsed] = useState(true);
  const { isDesktop } = useResponsive();
  const translation = useAppSelector((state) => state.translation);
  const user = useAppSelector((state) => state.user.user);
  const isAdmin = useAppSelector((state) => state.user.isAdmin);
  const dealRoomChat = useAppSelector((state) => state.dealRoom.dealRoomChat);
  const userPermission = useAppSelector((state) => state.user.user?.role?.permissions);
  const params = useParams();
  const location = useLocation();
  const toggleSider = () => setSiderCollapsed(!siderCollapsed);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [unreadMessage, setUnreadMessage] = useState(0);
  const [unreadDealRoom, setUnreadDealRoom] = useState(0);

  const getUnreadFile = useCallback(async () => {
    try {
      const data = await getUnreadMessage();
      setUnreadMessage(data.unreadMessages);
      dispatch(setUnreadMessages(data));
    } catch (error) {
      setUnreadMessage(0);
    }
  }, [dispatch]);

  const fetchUnreadDealRooms = useCallback(async () => {
    try {
      const data = await getUnreadDealRoom();
      setUnreadDealRoom(data.unread);
    } catch {
      setUnreadDealRoom(0);
    }
  }, []);

  const addNotification = useCallback(
    (user, isAdmin, data: IDealRoomMessage) => {
      let showChat = isAdmin;
      if (!showChat) {
        if (data.responsible.includes(user.id)) showChat = true;
        if (data.createdBy && data.createdBy === user.id) showChat = true;
      }
      if (showChat) {
        getUnreadFile();
        fetchUnreadDealRooms();
        dispatch(setDealRoomChat(dealRoomChat + 1));
        dispatch(setDealRoomMessage(data));
        notificationController.info({
          message: t('dealRooms.signals.messageInChat'),
          key: data.id,
          description: t(getAdminNotificationMessageKeyOnChat(data), {
            user: data.user,
            dealRoom: data.dealRoom,
          }),
          duration: 10,
          onClick: () => {
            navigate(`${ADMIN_BASE_BATH}/chats/${data.dealRoomId}`, { replace: true });
            dispatch(setDealRoomClickMessage(data));
            notification.close(data.id);
          },
        });
      }
    },
    [dealRoomChat, dispatch, fetchUnreadDealRooms, getUnreadFile, navigate, t],
  );

  useEffect(() => {
    (async () => {
      setIsTwoColumnsLayout([ADMIN_BASE_BATH].includes(location.pathname) && isDesktop);
      if (userPermission) {
        const path = getRoutePath(location, params);
        const isChat = path.startsWith(`${ADMIN_BASE_BATH}/chats`);
        if (isChat) {
          getUnreadFile();
        }
        if (typeof RouteWisePermission[path as keyof typeof RouteWisePermission] != 'undefined') {
          const permission = getPermissionType(location, params, userPermission);
          const hasAccessPermission = () => {
            return (
              permission === PermissionTypes.READ ||
              permission === PermissionTypes.READWRITE ||
              location.pathname === ADMIN_BASE_BATH
            );
          };

          if (!hasAccessPermission()) {
            navigate('/accessDenied');
          }
        }
      }
    })();
  }, [location.pathname, isDesktop, navigate, user, userPermission, location, params, getUnreadFile]);

  useEffect(() => {
    if (user) {
      socket.on(SOCKET_DEAL_ROOM_MESSAGE_FOR_ADMIN, (data: IDealRoomMessage) => {
        addNotification(user, isAdmin, data);
      });
      socket.on(SOCKET_DEAL_ROOM_UNREAD, () => {
        fetchUnreadDealRooms();
      });
      return () => {
        socket.off(SOCKET_DEAL_ROOM_MESSAGE_FOR_ADMIN);
        socket.off(SOCKET_DEAL_ROOM_UNREAD);
      };
    }
  }, [user, isAdmin, addNotification, fetchUnreadDealRooms]);

  useEffect(() => {
    getUnreadFile();
  }, [getUnreadFile]);

  useEffect(() => {
    fetchUnreadDealRooms();
  }, [fetchUnreadDealRooms]);

  return (
    <S.LayoutMaster className={contentDirection(translation.isRtl)}>
      <MainSider
        isCollapsed={siderCollapsed}
        setCollapsed={setSiderCollapsed}
        unreadMessage={unreadMessage}
        unreadDealRoom={unreadDealRoom}
      />
      <S.LayoutMain>
        {!isDesktop && (
          <MainHeader isTwoColumnsLayout={isTwoColumnsLayout}>
            <Header toggleSider={toggleSider} isSiderOpened={!siderCollapsed} isTwoColumnsLayout={isTwoColumnsLayout} />
          </MainHeader>
        )}
        <MainContent id="main-content" $isTwoColumnsLayout={isTwoColumnsLayout}>
          <div>
            <Outlet />
          </div>
        </MainContent>
      </S.LayoutMain>
    </S.LayoutMaster>
  );
};

export default MainLayout;
